import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LEVELS from "./levels";

/**
 * Лента уровней — главный блок кабинета.
 *
 * Состав карточки по Максиму 22.08.2026: картинка, название, заголовок,
 * число заданий и сколько людей уже прошло. Ниже — задания уровня: каждое
 * раскрывается по нажатию, у каждого свой статус и вознаграждение — игровые
 * монеты или цифровая награда.
 *
 * «Прошли N» — не украшение, а социальное доказательство: за то, что уже
 * прошли другие, берутся охотнее.
 *
 * ВАЖНО: числа и статусы образцовые. Уровней на платформе нет — есть квесты
 * внутри локаций, а это другая сущность. Поэтому под лентой стоит явная
 * оговорка: без неё человек примет чужой прогресс за свой. Когда появится
 * ручка, меняется источник массива, вёрстка остаётся.
 */


const STATE_LABEL = {
  done: "Пройден",
  active: "Сейчас",
  open: "Можно начать",
  locked: "Закрыт",
};

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const Chevron = ({ open }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={open ? "rot" : ""}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

/** «1 задание», «2 задания», «5 заданий» — иначе подпись читается машинной. */
const plural = (n, one, few, many) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

const Task = ({ task }) => {
  const [open, setOpen] = useState(false);

  return (
    <li className={`fw-task${task.done ? " done" : ""}`}>
      <button type="button" className="fw-task-head" onClick={() => setOpen(!open)}>
        <span className="dot">{task.done ? <Check /> : null}</span>
        <span className="fw-task-title">{task.title}</span>

        <span className="fw-task-reward">
          {task.coins ? <span className="coins">{task.coins} FDP</span> : null}
          {task.award ? <span className="award">{task.award}</span> : null}
        </span>

        <Chevron open={open} />
      </button>

      {open && (
        <div className="fw-task-body">
          <p>{task.how}</p>
          {task.to && (
            <Link className="fw-btn ghost" to={task.to}>
              Перейти
            </Link>
          )}
          {task.href && (
            <a className="fw-btn" href={task.href}>
              Забронировать →
            </a>
          )}
        </div>
      )}
    </li>
  );
};

const LevelDeck = () => {
  const [openKey, setOpenKey] = useState(
    (LEVELS.find((l) => l.state === "active") || LEVELS[0]).key
  );

  /* Баннер «С чего начать» просит раскрыть уровень — слушаем его событие.
     Так две соседние части страницы связаны напрямую, без состояния,
     протянутого через родителя. */
  /* Карточка, к которой человека только что привели кнопкой «С чего начать».
     Подсветка держится пару секунд и гаснет: постоянная рамка на первом
     уровне быстро становится шумом, а разовая — показывает, куда смотреть. */
  const [justOpened, setJustOpened] = useState(null);

  /* Подсказку показываем один раз. Тот, кто её закрыл, второй раз видеть
     её не должен — иначе она превращается в назойливое окно. */
  const [tipShown, setTipShown] = useState(
    () => localStorage.getItem("fw-levels-tip") !== "closed"
  );

  useEffect(() => {
    const onOpen = (e) => {
      const key = e && e.detail ? e.detail.key : null;
      if (key && LEVELS.some((l) => l.key === key)) {
        setOpenKey(key);
        setJustOpened(key);

        /* Прокрутка к самим заданиям, а не к заголовку блока: человек пришёл
           по кнопке «С чего начать» и должен увидеть, что делать, а не
           название раздела. Ждём кадр, чтобы панель успела раскрыться. */
        requestAnimationFrame(() => {
          const panel = document.getElementById("fw-level-tasks");
          if (panel) panel.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }
    };

    window.addEventListener("fw:open-level", onOpen);
    return () => window.removeEventListener("fw:open-level", onOpen);
  }, []);

  /* Гасим подсветку сами: если её не снять, рамка останется до перезагрузки
     страницы и будет спорить с обычным выделением выбранной карточки. */
  useEffect(() => {
    if (!justOpened) return undefined;
    const t = setTimeout(() => setJustOpened(null), 2600);
    return () => clearTimeout(t);
  }, [justOpened]);

  const closeTip = () => {
    setTipShown(false);
    localStorage.setItem("fw-levels-tip", "closed");
  };

  const open = LEVELS.find((l) => l.key === openKey) || null;
  const doneCount = open ? open.tasks.filter((t) => t.done).length : 0;

  return (
    <section className="fw-levels-block" id="fw-levels">
      <div className="fw-sec-head">
        <h2>Ваш путь по уровням</h2>
      </div>
      <p className="fw-sec-sub">
        Каждый пройденный уровень — плюс один к вашему. Выберите карточку, чтобы
        увидеть задания.
      </p>

      {/* Подсказка для новичка: объясняет, что перед ним не просто список,
          а маршрут. Закрывается крестиком и больше не возвращается. */}
      {tipShown && (
        <div className="fw-levels-tip" role="note">
          <span className="fw-levels-tip-ic" aria-hidden="true">✦</span>
          <div className="fw-levels-tip-body">
            <b>Это ваша дорожная карта</b>
            <span>
              Выполняйте задания уровня по порядку — так открываются новые
              возможности платформы и растёт доход. Интересных вам приключений
              и исполнения задуманного.
            </span>
          </div>
          <button
            type="button"
            className="fw-levels-tip-close"
            onClick={closeTip}
            aria-label="Скрыть подсказку"
          >
            ✕
          </button>
        </div>
      )}

      <div className="fw-levels">
        {LEVELS.map((l) => (
          <button
            key={l.key}
            type="button"
            className={
              `fw-level fw-level--${l.state}` +
              (l.key === openKey ? " selected" : "") +
              (l.key === justOpened ? " just-opened" : "")
            }
            onClick={() => setOpenKey(l.key)}
          >
            <span className="fw-level-art">
              {l.art ? (
                <img src={l.art} alt="" />
              ) : (
                <span className="fw-level-num">{l.n}</span>
              )}
              <span className="state">{STATE_LABEL[l.state]}</span>
            </span>

            <span className="fw-level-body">
              <b>{l.title}</b>
              <span className="fw-level-note">{l.lead}</span>

              <span className="fw-level-stats">
                <span>
                  {l.tasks.length} {plural(l.tasks.length, "задание", "задания", "заданий")}
                </span>
                <span>прошли {l.passed.toLocaleString("ru-RU")}</span>
              </span>
            </span>
          </button>
        ))}
      </div>

      {open && (
        <div id="fw-level-tasks" className="fw-card fw-roadmap">
          <div className="fw-roadmap-head">
            <b>
              Уровень {open.n} — {open.title}
            </b>
            <span>
              {doneCount} из {open.tasks.length}
            </span>
          </div>

          {open.intro ? <p className="fw-level-intro">{open.intro}</p> : null}

          {open.reward ? (
            <div className="fw-level-reward">
              <span className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="5" />
                  <path d="M8.5 12.5 7 22l5-3 5 3-1.5-9.5" />
                </svg>
              </span>
              <div>
                <b>За уровень: {open.reward.title}</b>
                <span>{open.reward.note}</span>
              </div>
            </div>
          ) : null}

          <ul className="fw-roadmap-list">
            {open.tasks.map((t) => (
              <Task key={t.title} task={t} />
            ))}
          </ul>
        </div>
      )}

      <p className="fw-sec-sub fw-levels-note">
        Задания отмечаются сами, когда вы их выполняете. Пока платформа не отдаёт
        прогресс, уровни и счётчики показаны как образец.
      </p>
    </section>
  );
};

export default LevelDeck;
